// frontend/src/components/AdminContentManager.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Footer } from './Footer';
import { 
  BookOpen, 
  Plus, 
  Edit, 
  Trash2, 
  Upload, 
  FileText, 
  Video, 
  Download,
  Eye,
  Play,
  AlertCircle
} from 'lucide-react';
import { apiUrl } from '../config/api';
import { ModuleEditor } from './ModuleEditor';

interface User {
  id: string;
  email: string;
  fullName: string;
  company: string;
  role: string;
  status: string;
}

interface AdminContentManagerProps {
  user: User;
  onLogout: () => void;
}

interface Module {
  _id: string;
  moduleId: number;
  title: string;
  description: string;
  duration: string;
  imageUrl: string;
  isPublished: boolean;
  order: number;
  prerequisites: number[];
}


interface UploadedFile {
  filename: string;
  originalName: string;
  size: number;
  type: string;
  url: string;
}

export function AdminContentManager({ user, onLogout }: AdminContentManagerProps) {
  const [activeTab, setActiveTab] = useState<'modules' | 'lessons' | 'files'>('modules');
  const [modules, setModules] = useState<Module[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Module editing state
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [showModuleForm, setShowModuleForm] = useState(false);
  
  // File upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  });

  const fetchModules = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(apiUrl('/api/modules'), {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        setModules(data.data);
        setError(null);
      } else {
        setError(data.message);
        // If it's an auth error, clear the token and redirect to login
        if (data.message.includes('token') || data.message.includes('auth') || response.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/auth';
        }
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
      setError('Failed to fetch modules');
    }
  }, []);

  const fetchLessons = useCallback(async (moduleId?: number) => {
    try {
      const url = moduleId ? `/api/lessons?moduleId=${moduleId}` : '/api/lessons';
      const response = await fetch(apiUrl(url), {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        // setLessons(data.data); // Removed unused state
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
      setError('Failed to fetch lessons');
    }
  }, []);

  const fetchUploadedFiles = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('/api/files'), {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        setUploadedFiles(data.data);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setError('Failed to fetch files');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (activeTab === 'modules') {
          await fetchModules();
        } else if (activeTab === 'lessons') {
          await fetchLessons();
        } else if (activeTab === 'files') {
          await fetchUploadedFiles();
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeTab, fetchModules, fetchLessons, fetchUploadedFiles]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(apiUrl('/api/files/upload'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        setUploadProgress(((i + 1) / files.length) * 100);
      }

      // Refresh file list
      await fetchUploadedFiles();
      setError(null);
    } catch (error) {
      console.error('Upload error:', error);
      setError(`Upload failed: ${error}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteFile = async (filename: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const response = await fetch(apiUrl(`/api/files/${filename}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchUploadedFiles();
      } else {
        setError('Failed to delete file');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError('Failed to delete file');
    }
  };

  const handleSaveModule = async (moduleData: Partial<Module>) => {
    try {
      const url = editingModule ? `/api/modules/${editingModule._id}` : '/api/modules';
      const method = editingModule ? 'PUT' : 'POST';

      const response = await fetch(apiUrl(url), {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(moduleData)
      });

      if (response.ok) {
        await fetchModules();
        setShowModuleForm(false);
        setEditingModule(null);
      } else {
        setError('Failed to save module');
      }
    } catch (error) {
      console.error('Save module error:', error);
      setError('Failed to save module');
    }
  };


  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf': return <FileText className="w-4 h-4 text-red-500" />;
      case 'video': return <Video className="w-4 h-4 text-blue-500" />;
      default: return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f8f9fa',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <main style={{ 
        paddingTop: '1.2rem',
        flex: 1
      }}>
        <div className="container">
          {/* Header */}
          <div style={{ marginBottom: '2rem' }}>
            <h1 className="admin-content-page-title" style={{
              fontSize: '2.5rem',
              fontWeight: 600,
              color: 'var(--dark)',
              marginBottom: '0.5rem',
              fontFamily: 'var(--montserrat)'
            }}>
              Content Management
            </h1>
            <p className="admin-content-page-description" style={{ color: 'var(--gray)', fontSize: '1.125rem' }}>
              Manage your course modules, lessons, and resources
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="admin-content-tab-navigation" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '2rem',
            border: '1px solid #eee'
          }}>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setActiveTab('modules')}
                className="admin-content-tab-button"
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: activeTab === 'modules' ? 'var(--orange)' : 'transparent',
                  color: activeTab === 'modules' ? 'var(--dark)' : 'var(--gray)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontFamily: 'var(--montserrat)'
                }}
              >
                <BookOpen style={{ width: '16px', height: '16px' }} />
                Modules
              </button>
              <button
                onClick={() => setActiveTab('lessons')}
                className="admin-content-tab-button"
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: activeTab === 'lessons' ? 'var(--orange)' : 'transparent',
                  color: activeTab === 'lessons' ? 'var(--dark)' : 'var(--gray)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontFamily: 'var(--montserrat)'
                }}
              >
                <Play style={{ width: '16px', height: '16px' }} />
                Lessons
              </button>
              <button
                onClick={() => setActiveTab('files')}
                className="admin-content-tab-button"
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: activeTab === 'files' ? 'var(--orange)' : 'transparent',
                  color: activeTab === 'files' ? 'var(--dark)' : 'var(--gray)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontFamily: 'var(--montserrat)'
                }}
              >
                <Upload style={{ width: '16px', height: '16px' }} />
                Files
              </button>
            </div>
          </div>

          {error && (
            <div style={{
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '2rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              color: '#991b1b'
            }}>
              <AlertCircle style={{ width: '20px', height: '20px' }} />
              <span>{error}</span>
            </div>
          )}

          {/* Modules Tab */}
          {activeTab === 'modules' && (
            <div>
              <div className="admin-content-card" style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #eee',
                overflow: 'hidden'
              }}>
                <div className="admin-module-header" style={{
                  padding: '1.5rem',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--dark)',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Course Modules
                  </h2>
                  <button
                    className="admin-module-actions"
                    onClick={() => {
                      setEditingModule(null);
                      setShowModuleForm(true);
                    }}
                    style={{
                      background: 'var(--orange)',
                      color: 'var(--dark)',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontFamily: 'var(--montserrat)'
                    }}
                  >
                    <Plus style={{ width: '16px', height: '16px' }} />
                    Add Module
                  </button>
                </div>

                {loading ? (
                  <div style={{
                    padding: '3rem',
                    textAlign: 'center',
                    color: 'var(--gray)'
                  }}>
                    Loading modules...
                  </div>
                ) : (
                  <div style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {modules.map((module) => (
                        <div
                          key={module._id}
                          className="admin-module-card"
                          style={{
                            border: '1px solid #eee',
                            borderRadius: '8px',
                            padding: '1.5rem',
                            background: '#f8f9fa'
                          }}
                        >
                          <div className="admin-module-card" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '1rem'
                          }}>
                            <div>
                              <h3 style={{
                                fontSize: '1.125rem',
                                fontWeight: 600,
                                color: 'var(--dark)',
                                marginBottom: '0.25rem'
                              }}>
                                Module {module.moduleId}: {module.title}
                              </h3>
                              <p style={{ color: 'var(--gray)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                                {module.description}
                              </p>
                              <div className="admin-module-meta" style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--gray)' }}>
                                <span>Duration: {module.duration}</span>
                                <span>Order: {module.order}</span>
                                <span style={{ 
                                  color: module.isPublished ? '#10b981' : '#f59e0b',
                                  fontWeight: 500
                                }}>
                                  {module.isPublished ? 'Published' : 'Draft'}
                                </span>
                              </div>
                            </div>
                            <div className="admin-module-card-actions" style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => {
                                  setEditingModule(module);
                                  setShowModuleForm(true);
                                }}
                                style={{
                                  background: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem',
                                  borderRadius: '6px',
                                  cursor: 'pointer'
                                }}
                              >
                                <Edit style={{ width: '14px', height: '14px' }} />
                              </button>
                              <button
                                onClick={() => {/* setSelectedModuleId(module.moduleId); // Removed unused state */}}
                                style={{
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem',
                                  borderRadius: '6px',
                                  cursor: 'pointer'
                                }}
                              >
                                <Eye style={{ width: '14px', height: '14px' }} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Files Tab */}
          {activeTab === 'files' && (
            <div>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #eee',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '1.5rem',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--dark)',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Uploaded Files
                  </h2>
                  <div>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.zip,.png,.jpg,.jpeg,.mp4,.mov"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      style={{
                        background: 'var(--orange)',
                        color: 'var(--dark)',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontFamily: 'var(--montserrat)'
                      }}
                    >
                      <Upload style={{ width: '16px', height: '16px' }} />
                      Upload Files
                    </label>
                  </div>
                </div>

                {uploading && (
                  <div style={{
                    padding: '1rem',
                    background: '#f0f9ff',
                    borderBottom: '1px solid #eee'
                  }}>
                    <div style={{
                      background: '#e5e7eb',
                      borderRadius: '4px',
                      height: '8px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        background: 'var(--orange)',
                        height: '100%',
                        width: `${uploadProgress}%`,
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
                      Uploading... {Math.round(uploadProgress)}%
                    </p>
                  </div>
                )}

                {loading ? (
                  <div style={{
                    padding: '3rem',
                    textAlign: 'center',
                    color: 'var(--gray)'
                  }}>
                    Loading files...
                  </div>
                ) : (
                  <div style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {uploadedFiles.map((file) => (
                        <div
                          key={file.filename}
                          className="admin-file-card"
                          style={{
                            border: '1px solid #eee',
                            borderRadius: '8px',
                            padding: '1rem',
                            background: '#f8f9fa',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div className="admin-file-meta" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            {getFileIcon(file.type)}
                            <div>
                              <p style={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: 'var(--dark)',
                                marginBottom: '0.25rem'
                              }}>
                                {file.originalName}
                              </p>
                              <p style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>
                                {formatFileSize(file.size)} • {file.type.toUpperCase()}
                              </p>
                            </div>
                          </div>
                          <div className="admin-file-card-actions" style={{ display: 'flex', gap: '0.5rem' }}>
                            <a
                              href={apiUrl(`/api/files/${file.filename}`)}
                              download
                              style={{
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                textDecoration: 'none'
                              }}
                            >
                              <Download style={{ width: '14px', height: '14px' }} />
                            </a>
                            <button
                              onClick={() => handleDeleteFile(file.filename)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              <Trash2 style={{ width: '14px', height: '14px' }} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Module Editor Modal */}
      {showModuleForm && (
        <ModuleEditor
          module={editingModule}
          onSave={handleSaveModule}
          onCancel={() => {
            setShowModuleForm(false);
            setEditingModule(null);
          }}
        />
      )}
    </div>
  );
}

export default AdminContentManager;
